import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Download, Trash2, FileText, ChevronDown, X, Send, Landmark, Repeat, CalendarClock, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Field } from "./Clients";
import { fmtMelDate, fmtMelDateShort } from "../lib/utils";
import { PageBand, TileRow, StatTile, OrangeButton } from "../components/layout/PageShell";

const fmtAud = (n) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n || 0);
const STATUSES = ["draft", "sent", "paid", "overdue"];
// Overdue is computed from the due date — a trainer can only pick these.
const SETTABLE_STATUSES = ["draft", "sent", "paid"];
const fmtDueShort = fmtMelDateShort;
const STATUS_STYLES = {
  draft: "bg-[#EEE9E0] text-[#6B635C] hover:bg-[#E8E1D6]",
  sent: "bg-[#E1EDF7] text-[#1F5C8F] hover:bg-[#D3E4F4]",
  paid: "bg-[#E3F1E9] text-[#1D6B4A] hover:bg-[#D7EBDF]",
  overdue: "bg-[#F9E4DF] text-[#A8372A] hover:bg-[#F3D6D0]",
};

const newLineKey = () =>
  typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `ln_${Math.random().toString(36).slice(2)}`;
const makeDefaultLine = () => ({
  _key: newLineKey(),
  description: "Personal training session",
  quantity: 1,
  unit_price: 90,
});

export default function Invoices() {
  const { readOnly } = useAuth();
  const [params, setParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [bankSettings, setBankSettings] = useState(null);
  const [removed, setRemoved] = useState([]);
  const [removedOpen, setRemovedOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingDue, setPendingDue] = useState(null);
  const [dueValue, setDueValue] = useState("");
  const [confirmSendAll, setConfirmSendAll] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [form, setForm] = useState({
    client_id: "",
    service_date: new Date().toISOString().slice(0, 10),
    lines: [makeDefaultLine()],
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [{ data: inv }, { data: cl }, { data: st }, { data: rm }] = await Promise.all([
      api.get("/invoices"),
      api.get("/clients"),
      api.get("/settings"),
      api.get("/invoices/removed"),
    ]);
    setInvoices(inv);
    setClients(cl);
    setBankSettings(st);
    setRemoved(rm);
  }, []);
  useEffect(() => { load(); }, [load]);

  const showBankReminder = useMemo(() => {
    if (!bankSettings) return false;
    return !(bankSettings.account_name || bankSettings.bsb || bankSettings.account_number);
  }, [bankSettings]);

  const waitingOnAbn = useMemo(
    () => invoices.filter((i) => i.awaiting_abn && i.status === "draft"),
    [invoices],
  );
  const abnValid = String(bankSettings?.abn || "").replace(/\D/g, "").length === 11;

  // The Draft chip only earns its place while drafts exist.
  const hasDrafts = useMemo(() => invoices.some((i) => i.status === "draft"), [invoices]);
  const visibleStatuses = useMemo(
    () => STATUSES.filter((s) => s !== "draft" || hasDrafts),
    [hasDrafts],
  );
  useEffect(() => {
    if (filter === "draft" && !hasDrafts) setFilter("all");
  }, [filter, hasDrafts]);

  const sendAllWaiting = async () => {
    setSendingAll(true);
    try {
      const { data } = await api.post("/invoices/send-waiting");
      setConfirmSendAll(false);
      if (data.failed?.length) {
        toast.warning(`${data.sent} sent. ${data.failed.length} could not be sent.`);
      } else {
        toast.success(`${data.sent} invoice${data.sent === 1 ? "" : "s"} sent`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invoices");
    } finally {
      setSendingAll(false);
    }
  };

  const openDueEditor = (inv) => {
    setPendingDue(inv);
    setDueValue(String(inv.due_date || "").slice(0, 10) || new Date().toISOString().slice(0, 10));
  };

  const saveDueDate = async () => {
    if (!pendingDue) return;
    try {
      await api.patch(`/invoices/${pendingDue.invoice_id}/due-date`, { due_date: dueValue });
      toast.success("Due date updated");
      setPendingDue(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the due date");
    }
  };

  const openCreate = useCallback(() => {
    setForm({
      client_id: clients[0]?.client_id || "",
      service_date: new Date().toISOString().slice(0, 10),
      lines: [makeDefaultLine()],
      notes: "",
    });
    setOpen(true);
  }, [clients]);

  const handledNewParam = useRef(false);
  useEffect(() => {
    if (handledNewParam.current) return;
    if (clients.length === 0) return; // wait for clients so first one can be pre-selected
    if (params.get("new")) {
      handledNewParam.current = true;
      openCreate();
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams, openCreate, clients.length]);

  // Deep link from a PACK billing chip: ?focus=<invoice_id> scrolls to and
  // briefly highlights that invoice row. Display-only.
  const [focusId, setFocusId] = useState(null);
  const handledFocusParam = useRef(false);
  useEffect(() => {
    if (handledFocusParam.current) return;
    const f = params.get("focus");
    if (!f) return;
    handledFocusParam.current = true;
    setFocusId(f);
    params.delete("focus");
    setParams(params, { replace: true });
  }, [params, setParams]);
  useEffect(() => {
    if (!focusId || invoices.length === 0) return;
    const el = document.getElementById(`inv-row-${focusId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setFocusId(null), 2800);
    return () => clearTimeout(t);
  }, [focusId, invoices]);

  const clientName = (id) => clients.find((c) => c.client_id === id)?.name || "—";

  const filtered = useMemo(() => filter === "all" ? invoices : invoices.filter((i) => i.status === filter), [invoices, filter]);

  const totals = useMemo(() => {
    // Drafts have never been sent, so they are not owed yet — excluded here.
    const unpaidList = invoices.filter((i) => ["sent", "overdue"].includes(i.status));
    const unpaid = unpaidList.reduce((a, b) => a + b.total, 0);
    const paid = invoices.filter((i) => i.status === "paid").reduce((a, b) => a + b.total, 0);
    const overdue = invoices.filter((i) => i.status === "overdue").reduce((a, b) => a + b.total, 0);
    const draftCount = invoices.filter((i) => i.status === "draft").length;
    return { unpaid, paid, overdue, unpaidCount: unpaidList.length, draftCount };
  }, [invoices]);

  const gstEnabled = bankSettings ? bankSettings.gst_enabled !== false : true;
  const total = form.lines.reduce((acc, l) => acc + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
  const subtotal = gstEnabled ? Math.round((total / 1.10) * 100) / 100 : total;
  const gst = gstEnabled ? Math.round((total - subtotal) * 100) / 100 : 0;

  const updateLine = (idx, patch) => {
    setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  };
  const addLine = () => {
    setForm((f) => ({ ...f, lines: [...f.lines, makeDefaultLine()] }));
  };
  const removeLine = (idx) => {
    setForm((f) => ({ ...f, lines: f.lines.length > 1 ? f.lines.filter((_, i) => i !== idx) : f.lines }));
  };

  const save = async () => {
    if (!form.client_id) { toast.error("Pick a client"); return; }
    const cleaned = form.lines
      .map((l) => ({
        description: (l.description || "").trim(),
        quantity: Number(l.quantity) || 0,
        unit_price: Number(l.unit_price) || 0,
      }))
      .filter((l) => l.description && l.quantity > 0);
    if (cleaned.length === 0) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      await api.post("/invoices", {
        client_id: form.client_id,
        service_date: new Date(form.service_date).toISOString(),
        lines: cleaned,
        notes: form.notes,
      });
      toast.success("Invoice created");
      setOpen(false);
      await load();
    } catch { toast.error("Could not create invoice"); }
    finally { setSaving(false); }
  };

  const setStatus = async (inv, status) => {
    if (inv.status === status) return;
    try {
      await api.put(`/invoices/${inv.invoice_id}/status`, { status });
      toast.success(`Marked as ${status}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update status"); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const inv = pendingDelete;
    setPendingDelete(null);
    try {
      await api.delete(`/invoices/${inv.invoice_id}`);
      toast.success(`${inv.invoice_number} deleted`);
      await load();
    } catch { toast.error("Could not delete invoice"); }
  };

  const emailInvoice = async (inv) => {
    if (inv.status === "sent" || inv.status === "paid") return;
    try {
      await api.put(`/invoices/${inv.invoice_id}/status`, { status: "sent" });
      toast.success("Invoice marked as Sent. Email coming soon.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update invoice"); }
  };

  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.invoice_id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_number}-TrainMate.pdf`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <PageBand
        overlap
        flushPhone
        testid="invoices-band"
        title={
          <div>
            <p style={{ fontSize: 12, fontWeight: 600 }} className="uppercase tracking-widest text-[#D3E8E6]">
              AUD
            </p>
            <h1 className="font-bold leading-tight mt-1" style={{ fontSize: 22, letterSpacing: "-0.02em" }}>
              Invoices
            </h1>
          </div>
        }
        right={
          <OrangeButton
            onClick={openCreate}
            data-testid="add-invoice-button"
            disabled={clients.length === 0 || readOnly}
            title={readOnly ? "Your account is read only" : undefined}
            className="w-full sm:w-auto justify-center disabled:opacity-40"
            style={{ minHeight: 44 }}
          >
            <Plus className="w-4 h-4" /> New Invoice
          </OrangeButton>
        }
      />

      <TileRow flushPhone cols="grid-cols-2 lg:grid-cols-3">
        <StatTile
          className="col-span-2 lg:col-span-1"
          big
          testid="invoice-unpaid-total"
          label="Unpaid"
          value={fmtAud(totals.unpaid)}
          tone="text-[#8A6B1E]"
          bg="#FBF4DD"
          accent="#E9A94B"
          hint={
            <>
              <span data-testid="invoice-unpaid-count">
                {`${totals.unpaidCount} invoice${totals.unpaidCount === 1 ? "" : "s"}, incl. overdue`}
              </span>
              {totals.draftCount > 0 ? (
                <span className="block text-[#8E867E] mt-0.5" data-testid="invoice-draft-note">
                  {`Plus ${totals.draftCount} draft${totals.draftCount === 1 ? "" : "s"} not sent yet`}
                </span>
              ) : null}
            </>
          }
        />
        <StatTile
          testid="invoice-overdue-total"
          label="Overdue"
          value={fmtAud(totals.overdue)}
          tone={totals.overdue > 0 ? "text-[#A8372A]" : "text-[#6B635C]"}
          bg={totals.overdue > 0 ? "#F9E4DF" : "#FFFFFF"}
          accent={totals.overdue > 0 ? "#A8372A" : undefined}
          hint="Past their due date"
        />
        <StatTile
          testid="invoice-paid-total"
          label="Paid"
          value={fmtAud(totals.paid)}
          tone="text-[#1D6B4A]"
          bg="#E3F1E9"
          accent="#1D6B4A"
          hint="All-time, GST inclusive"
        />
      </TileRow>

      <p className="text-xs text-[#6B635C] max-w-xl" data-testid="invoice-numbers-help">
        Invoice numbers are never reused. If a number is missing, that invoice was removed.
        {removed.length > 0 && (
          <>
            {" "}
            <button
              type="button"
              onClick={() => {
                setRemovedOpen(true);
                requestAnimationFrame(() =>
                  document
                    .getElementById("removed-numbers")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" }),
                );
              }}
              data-testid="removed-numbers-link"
              className="underline underline-offset-2 text-[#6B635C] hover:text-[#0F6B6B] transition-colors"
            >
              See removed numbers below.
            </button>
          </>
        )}
      </p>

      {waitingOnAbn.length > 0 && (
        <div
          data-testid="abn-waiting-banner"
          className="tm-card px-5 py-4 border-l-4 border-l-[#D9A441] bg-[#FBF4DD] flex items-start sm:items-center gap-4 flex-wrap sm:flex-nowrap"
        >
          <div className="w-9 h-9 rounded-md bg-[#F6ECC9] border border-[#ECDCA6] flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-[#7A5E18]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#7A5E18]">
              {abnValid ? (
                <>
                  {waitingOnAbn.length} invoice{waitingOnAbn.length === 1 ? " is" : "s are"} ready to send
                </>
              ) : (
                <>
                  {waitingOnAbn.length} invoice{waitingOnAbn.length === 1 ? " is" : "s are"} waiting for your ABN. Add it in Settings
                </>
              )}
            </p>
            <p className="text-xs text-[#6B635C] mt-1">
              {abnValid
                ? "GST is on, so these go out as tax invoices with your ABN. They're still drafts — nothing has been emailed."
                : "GST is on, so these have to go out as tax invoices. They stay as drafts — nothing has been emailed."}
            </p>
          </div>
          {abnValid ? (
            <Button
              onClick={() => setConfirmSendAll(true)}
              disabled={readOnly}
              title={readOnly ? "Your account is read only" : undefined}
              data-testid="abn-send-all-btn"
              className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold whitespace-nowrap disabled:opacity-40"
            >
              <Send className="w-4 h-4" /> Send all {waitingOnAbn.length}
            </Button>
          ) : (
            <Link
              to="/settings"
              data-testid="abn-waiting-cta"
              className="text-xs uppercase tracking-widest font-bold px-3 py-2 rounded-md bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] transition-colors whitespace-nowrap"
            >
              Add ABN
            </Link>
          )}
        </div>
      )}

      {showBankReminder && (
        <div
          data-testid="bank-reminder-banner"
          className="tm-card px-5 py-4 border-l-4 border-l-[#D9A441] bg-[#FBF4DD] flex items-start sm:items-center gap-4 flex-wrap sm:flex-nowrap"
        >
          <div className="w-9 h-9 rounded-md bg-[#F6ECC9] border border-[#ECDCA6] flex items-center justify-center shrink-0">
            <Landmark className="w-4 h-4 text-[#7A5E18]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#7A5E18]">Add your bank details so clients can pay you</p>
            <p className="text-xs text-[#6B635C] mt-1">Your invoice PDFs go out without a &quot;How to pay&quot; section until you add an account name, BSB, and account number.</p>
          </div>
          <Link
            to="/settings"
            data-testid="bank-reminder-cta"
            className="text-xs uppercase tracking-widest font-bold px-3 py-2 rounded-md bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] transition-colors whitespace-nowrap"
          >
            Add now
          </Link>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="invoice-filter-bar">
        {["all", ...visibleStatuses].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`filter-${s}`}
            className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-widest font-bold border transition-colors ${
              filter === s
                ? "bg-[#0F6B6B] text-[#FFFFFF] border-[#0F6B6B]"
                : "bg-transparent text-[#2A2623] border-[#E8E1D6] hover:text-[#2A2623] hover:border-[#C9BFAF]"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="tm-card overflow-hidden" data-testid="invoices-table">
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-xs uppercase tracking-widest text-[#6B635C] border-b" style={{ borderColor: "#E8E1D6" }}>
          <div className="col-span-2">Number</div>
          <div className="col-span-3">Client</div>
          <div className="col-span-2">Issue date</div>
          <div className="col-span-2 text-right">Total</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-[#6B635C]">
            <FileText className="w-8 h-8 mx-auto mb-3 text-[#6B635C]" />
            No invoices {filter === "all" ? "yet" : `in "${filter}"`}.
          </div>
        ) : filtered.map((inv) => (
          <div key={inv.invoice_id} id={`inv-row-${inv.invoice_id}`} className={`flex items-start gap-3 md:grid md:grid-cols-12 md:gap-4 md:items-center px-4 md:px-5 py-3 md:py-4 border-b text-sm hover:bg-[#F3EDE4] transition-all ${focusId === inv.invoice_id ? "ring-2 ring-[#9BC2E4] rounded-md bg-[#E1EDF7]" : ""}`} style={{ borderColor: "#E8E1D6" }} data-testid={`invoice-row-${inv.invoice_id}`}>
            <div className="md:contents flex-1 min-w-0 flex flex-col gap-0.5">
            <div className="md:col-span-2 font-bold font-display text-base">
              {inv.invoice_number}
              {inv.recurring_series_id && (
                <span
                  data-testid={`invoice-recurring-tag-${inv.invoice_id}`}
                  className="inline-flex items-center gap-1 ml-2 align-middle text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#0F6B6B]/10 text-[#0F6B6B] border border-[#0F6B6B]/25"
                  title={`Auto-generated from a recurring series (week ${inv.series_week} of ${inv.series_total_weeks}).`}
                >
                  <Repeat className="w-3 h-3" /> Recurring · W{inv.series_week}/{inv.series_total_weeks}
                </span>
              )}
            </div>
            <div className="md:col-span-3 truncate">{clientName(inv.client_id)}</div>
            <div className="md:col-span-2 text-[#6B635C]">
              {fmtMelDate(inv.created_at)}
              <span className="block mt-0.5 text-xs" data-testid={`invoice-due-${inv.invoice_id}`}>
                {inv.due_date ? (
                  <span className={inv.status === "overdue" ? "text-[#A8372A]" : "text-[#2A2623]"}>
                    Due {fmtDueShort(inv.due_date)}
                  </span>
                ) : inv.status === "draft" ? (
                  <span className="text-[#6B635C]">Due date set when sent</span>
                ) : (
                  <span className="text-[#6B635C]">No due date</span>
                )}
                {inv.status !== "paid" && (
                  <button
                    type="button"
                    onClick={() => openDueEditor(inv)}
                    disabled={readOnly}
                    data-testid={`invoice-edit-due-${inv.invoice_id}`}
                    title={readOnly ? "Your account is read only" : "Change the due date"}
                    aria-label={`Change the due date on invoice ${inv.invoice_number}`}
                    className="ml-2 inline-flex items-center justify-center align-middle min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 text-[#6B635C] hover:text-[#0F6B6B] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-[#6B635C]"
                  >
                    <CalendarClock className="w-3.5 h-3.5" />
                  </button>
                )}
              </span>
              <span
                className="md:hidden block mt-0.5 text-xs text-[#6B635C]"
                data-testid={`invoice-gst-note-m-${inv.invoice_id}`}
              >
                {inv.gst_applied === false ? "No GST" : `incl. GST ${fmtAud(inv.gst)}`}
              </span>
            </div>
            </div>
            <div className="md:contents flex flex-col items-end gap-1.5 shrink-0 text-right">
            <div className="md:col-span-2 md:text-right font-bold text-[#0F6B6B]">
              {fmtAud(inv.total)}
              <span
                className="text-xs text-[#6B635C] font-normal hidden md:block"
                data-testid={`invoice-gst-note-${inv.invoice_id}`}
              >
                {inv.gst_applied === false ? "No GST" : `incl. GST ${fmtAud(inv.gst)}`}
              </span>
            </div>
            <div className="md:col-span-2">
              <StatusDropdown invoice={inv} onChange={setStatus} readOnly={readOnly} />
              {inv.status === "overdue" && (
                <span
                  data-testid={`invoice-overdue-chip-${inv.invoice_id}`}
                  className="block mt-1 w-fit text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#F9E4DF] text-[#A8372A] border border-[#F0C9C1]"
                >
                  Overdue {inv.overdue_days ?? 0} day{(inv.overdue_days ?? 0) === 1 ? "" : "s"}
                </span>
              )}
              {inv.awaiting_abn && inv.status === "draft" && (
                <span
                  data-testid={`invoice-awaiting-abn-${inv.invoice_id}`}
                  className="block mt-1 w-fit text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#FBF4DD] text-[#7A5E18] border border-[#ECDCA6]"
                >
                  Add your ABN to send
                </span>
              )}
            </div>
            <div className="md:col-span-1 flex md:justify-end gap-1">
              {(() => {
                const sent = inv.status === "sent" || inv.status === "paid";
                const disabled = sent || readOnly;
                return (
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => emailInvoice(inv)}
                    disabled={disabled}
                    title={readOnly ? "Your account is read only" : sent ? "Sent" : "Email this invoice"}
                    aria-label={sent ? `Invoice ${inv.invoice_number} already sent` : `Email invoice ${inv.invoice_number}`}
                    data-testid={`invoice-email-${inv.invoice_id}`}
                    className={`h-11 w-11 md:h-8 md:w-8 ${disabled ? "text-[#6B635C] cursor-not-allowed opacity-50" : "text-[#6B635C] hover:text-[#0F6B6B]"}`}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                );
              })()}
              <Button size="icon" variant="ghost" onClick={() => downloadPdf(inv)} title="Download PDF" data-testid={`invoice-pdf-${inv.invoice_id}`} className="h-11 w-11 md:h-8 md:w-8 text-[#6B635C] hover:text-[#0F6B6B]"><Download className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => setPendingDelete(inv)} disabled={readOnly} title={readOnly ? "Your account is read only" : "Delete"} data-testid={`invoice-delete-${inv.invoice_id}`} className="h-11 w-11 md:h-8 md:w-8 text-[#6B635C] hover:text-[#A8372A] disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 className="w-4 h-4" /></Button>
            </div>
            </div>
          </div>
        ))}
      </div>

      {removed.length > 0 && (
        <section id="removed-numbers" className="tm-card overflow-hidden" data-testid="removed-numbers-section">
          <button
            type="button"
            onClick={() => setRemovedOpen((v) => !v)}
            data-testid="removed-numbers-toggle"
            aria-expanded={removedOpen}
            className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-[#F3EDE4] transition-colors"
          >
            <span className="text-xs uppercase tracking-widest text-[#6B635C]">
              Removed numbers
              <span className="ml-2 text-[#6B635C]">({removed.length})</span>
            </span>
            <ChevronDown className={`w-4 h-4 text-[#6B635C] transition-transform ${removedOpen ? "rotate-180" : ""}`} />
          </button>
          {removedOpen && (
            <div className="border-t" style={{ borderColor: "#E8E1D6" }} data-testid="removed-numbers-list">
              <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-xs uppercase tracking-widest text-[#6B635C] border-b" style={{ borderColor: "#E8E1D6" }}>
                <div className="col-span-2">Number</div>
                <div className="col-span-4">Client</div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-2">Removed</div>
                <div className="col-span-2">Reason</div>
              </div>
              {removed.map((r, i) => (
                <div
                  key={`${r.invoice_number}-${r.removed_at}-${i}`}
                  data-testid={`removed-number-${r.invoice_number}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-4 px-5 py-3 border-b text-sm items-center last:border-b-0"
                  style={{ borderColor: "#E8E1D6" }}
                >
                  <div className="col-span-2 font-mono text-[#2A2623]">{r.invoice_number}</div>
                  <div className="col-span-4 text-[#6B635C] truncate">{r.client_name || "—"}</div>
                  <div className="col-span-2 md:text-right text-[#6B635C]">{fmtAud(r.total)}</div>
                  <div className="col-span-2 text-[#6B635C] text-xs">{fmtMelDate(r.removed_at)}</div>
                  <div className="col-span-2 text-[#6B635C] text-xs">{r.reason}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}


      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">New invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Client *">
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="invoice-client" className="bg-[#FAF7F2] border-[#E8E1D6]"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]">
                    {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Service date">
                <Input type="date" value={form.service_date} onChange={(e) => setForm({ ...form, service_date: e.target.value })} data-testid="invoice-date" className="bg-[#FAF7F2] border-[#E8E1D6] text-[#2A2623]" />
              </Field>
            </div>

            <div data-testid="invoice-lines">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-widest text-[#6B635C]">Line items</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addLine}
                  data-testid="invoice-add-line"
                  className="h-7 border-[#E8E1D6] bg-transparent text-[#0F6B6B] hover:bg-[#FAF7F2] hover:text-[#0F6B6B] text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add line
                </Button>
              </div>

              <div className="hidden sm:grid grid-cols-12 gap-2 px-1 pb-1 text-[10px] uppercase tracking-widest text-[#6B635C]">
                <div className="col-span-6">Description</div>
                <div className="col-span-2 text-right">Qty</div>
                <div className="col-span-2 text-right">Unit (incl. GST)</div>
                <div className="col-span-2 text-right pr-7">Line</div>
              </div>

              <div className="space-y-2">
                {form.lines.map((ln, idx) => {
                  const lineSub = Number(ln.quantity || 0) * Number(ln.unit_price || 0);
                  return (
                    <div
                      key={ln._key || `line-${idx}`}
                      className="grid grid-cols-12 gap-2 items-center"
                      data-testid={`invoice-line-${idx}`}
                    >
                      <Input
                        value={ln.description}
                        onChange={(e) => updateLine(idx, { description: e.target.value })}
                        data-testid={`invoice-line-${idx}-desc`}
                        placeholder="Description"
                        className="col-span-12 sm:col-span-6 bg-[#FAF7F2] border-[#E8E1D6] text-sm"
                      />
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={ln.quantity}
                        onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                        data-testid={`invoice-line-${idx}-qty`}
                        className="col-span-4 sm:col-span-2 bg-[#FAF7F2] border-[#E8E1D6] text-sm text-right"
                      />
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={ln.unit_price}
                        onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                        data-testid={`invoice-line-${idx}-price`}
                        className="col-span-4 sm:col-span-2 bg-[#FAF7F2] border-[#E8E1D6] text-sm text-right"
                      />
                      <div className="col-span-3 sm:col-span-2 text-right text-sm font-bold text-[#0F6B6B] truncate" data-testid={`invoice-line-${idx}-subtotal`}>
                        {fmtAud(lineSub)}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        disabled={form.lines.length === 1}
                        data-testid={`invoice-line-${idx}-remove`}
                        title="Remove line"
                        className="col-span-1 justify-self-end h-8 w-8 rounded-md text-[#6B635C] hover:text-[#A8372A] hover:bg-[#FAF7F2] disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <Field label="Notes">
              <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="invoice-notes" className="w-full rounded-md bg-[#FAF7F2] border border-[#E8E1D6] px-3 py-2 text-sm" />
            </Field>

            <div className="rounded-md border border-[#E8E1D6] bg-[#FAF7F2] p-3 text-sm space-y-1" data-testid="invoice-totals">
              {gstEnabled ? (
                <>
                  <Row label="Subtotal (ex-GST)" value={fmtAud(subtotal)} />
                  <Row label="GST (10%) incl." value={fmtAud(gst)} />
                </>
              ) : (
                <p className="text-xs text-[#6B635C]" data-testid="invoice-no-gst-note">GST not applicable</p>
              )}
              <Row label="Total (AUD)" value={fmtAud(total)} bold />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="invoice-cancel" className="border-[#E8E1D6] bg-transparent hover:bg-[#FAF7F2]">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="invoice-save" className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold">
              {saving ? "Creating..." : "Create invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingDue} onOpenChange={(o) => !o && setPendingDue(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623] max-w-sm" data-testid="due-date-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase">Due date</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#6B635C]">
              {pendingDue?.invoice_number} · {pendingDue?.payment_terms_days != null
                ? pendingDue.payment_terms_days === 0
                  ? "Due on receipt when issued"
                  : `${pendingDue.payment_terms_days} day terms when issued`
                : "No terms recorded"}
            </p>
            <Field label="Due date">
              <Input
                type="date"
                value={dueValue}
                onChange={(e) => setDueValue(e.target.value)}
                data-testid="due-date-input"
                className="bg-[#FAF7F2] border-[#E8E1D6] text-[#2A2623] w-full"
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDue(null)} data-testid="due-date-cancel" className="border-[#E8E1D6] bg-transparent hover:bg-[#FAF7F2]">Cancel</Button>
            <Button onClick={saveDueDate} data-testid="due-date-save" className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmSendAll} onOpenChange={(o) => !o && setConfirmSendAll(false)}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="send-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623]">
              Send {waitingOnAbn.length} invoice{waitingOnAbn.length === 1 ? "" : "s"} now?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B635C]">
              Each one goes out as a tax invoice with your ABN and a due date based on your payment terms.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="send-all-cancel" className="border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); sendAllWaiting(); }}
              disabled={sendingAll}
              data-testid="send-all-confirm"
              className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold"
            >
              {sendingAll ? "Sending..." : "Send them"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="invoice-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623]">Delete invoice?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B635C]">
              Are you sure you want to delete this invoice
              {pendingDelete ? ` (${pendingDelete.invoice_number})` : ""}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="invoice-delete-cancel"
              className="border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="invoice-delete-confirm"
              className="bg-[#A8372A] hover:bg-[#8E2E23] text-[#FFFFFF]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-bold text-[#0F6B6B] pt-1 border-t border-[#E8E1D6]" : "text-[#2A2623]"}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

function StatusDropdown({ invoice, onChange, readOnly }) {
  if (readOnly) {
    return (
      <button
        type="button"
        disabled
        data-testid={`invoice-status-trigger-${invoice.invoice_id}`}
        title="Your account is read only"
        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-3 md:px-2 py-2.5 md:py-1 min-h-[44px] md:min-h-0 rounded font-bold opacity-50 cursor-not-allowed ${STATUS_STYLES[invoice.status]}`}
      >
        <span data-testid={`status-${invoice.status}`}>{invoice.status}</span>
      </button>
    );
  }
  // Draft is only an option while the invoice is still a draft. Once it's
  // been sent, paid or marked overdue, Draft is removed from the menu.
  const options = SETTABLE_STATUSES.filter(
    (s) => !(s === "draft" && ["sent", "paid", "overdue"].includes(invoice.status))
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`invoice-status-trigger-${invoice.invoice_id}`}
          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-3 md:px-2 py-2.5 md:py-1 min-h-[44px] md:min-h-0 rounded font-bold transition-colors ${STATUS_STYLES[invoice.status]}`}
          title="Change status"
        >
          <span data-testid={`status-${invoice.status}`}>{invoice.status}</span>
          <ChevronDown className="w-3 h-3 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        data-testid={`invoice-status-menu-${invoice.invoice_id}`}
        className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623] min-w-[140px]"
      >
        {options.map((s) => (
          <DropdownMenuItem
            key={s}
            onSelect={() => onChange(invoice, s)}
            data-testid={`invoice-status-option-${invoice.invoice_id}-${s}`}
            className={`text-xs uppercase tracking-widest font-bold cursor-pointer focus:bg-[#FAF7F2] focus:text-[#2A2623] ${
              s === invoice.status ? "text-[#0F6B6B]" : "text-[#2A2623]"
            }`}
          >
            {s}
            {s === invoice.status && <span className="ml-auto text-[10px] opacity-70">current</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
